import { AccountCollectionFlowResponse } from 'src/queries/payments/payments.types'

/**
 * Presentation status derived from the server-authoritative payment method.
 * The API does not persist either value as a Vendor status.
 */
export enum VendorStatus {
  ActionRequired = 'ACTION_REQUIRED',
  Active = 'ACTIVE',
}

export interface VendorPaymentMethodResponse {
  id: string
  type: 'ACH'
  status: 'PAYABLE'
}

export interface VendorAccountCollectionFlowState {
  flowId: string
  status: 'PENDING' | 'AWAITING_ACKNOWLEDGEMENT'
  acknowledgementCopyVersion: string
}

/**
 * Realtime, display-safe projection of a vendor's payment method, fetched live
 * from Modern Treasury (`GET /payments/vendors/{vendorId}/payment-methods/
 * {paymentMethodId}`). Mirrors `VendorPaymentMethodDetailsResponse` in
 * extend-api (`VendorSchema.kt`). Raw account numbers never cross this
 * boundary; `accountNumberLast4` is MT's safe representation, and every bank
 * field is nullable — render a graceful fallback, never `undefined`.
 */
export interface VendorPaymentMethodDetailsResponse {
  id: string
  type: string
  status: string
  bankName?: string | null
  accountType?: string | null
  accountNumberLast4?: string | null
  routingNumber?: string | null
}

/** Plain app-domain Vendor cached by React Query. */
export interface Vendor {
  id: string
  name: string
  email: string
  externalId?: string
  contactName?: string
  phone?: string
  notes?: string
  status: VendorStatus
  paymentMethod: VendorPaymentMethodResponse | null
  accountCollectionFlow?: VendorAccountCollectionFlowState
  createdAt: string
  updatedAt: string
}

/**
 * Wire DTO returned by the API. NON_NULL serialization means every nullable
 * field may arrive as either null or be omitted entirely.
 */
export interface VendorResponse {
  id: string
  name: string
  email: string
  externalId?: string | null
  contactName?: string | null
  phoneNumber?: string | null
  description?: string | null
  paymentMethod?: VendorPaymentMethodResponse | null
  accountCollectionFlow?: VendorAccountCollectionFlowState | null
  createdAt: string
  updatedAt: string
}

export interface VendorPagination {
  page: number
  pageItemCount: number
  totalItems: number
  numberOfPages: number
}

export interface VendorsPageResponse {
  vendors: VendorResponse[]
  pagination: VendorPagination
}

export interface VendorsList {
  vendors: Vendor[]
  pagination: VendorPagination
}

export interface ListVendorsParams {
  page: number
  count: number
  search?: string
}

export interface CreateVendorRequest {
  name: string
  email: string
  externalId?: string
  contactName?: string
  phone?: string
  notes?: string
}

export interface CreateVendorMutationInput {
  request: CreateVendorRequest
  idempotencyKey: string
}

export type VendorAccountCollectionFlowResponse =
  AccountCollectionFlowResponse & {
    acknowledgementCopyVersion: string
  }

export interface ConfirmVendorAccountCollectionFlowInput {
  vendorId: string
  flowId: string
  acknowledged: true
  acknowledgementCopyVersion: string
}

export interface CompleteVendorAccountCollectionFlowInput {
  vendorId: string
  flowId: string
}

export type VendorAccountCollectionFlowCompletionResponse = {
  status: 'PENDING' | 'AWAITING_ACKNOWLEDGEMENT' | 'CONFIRMED'
}

export type VendorAccountCollectionFlowConfirmationResponse =
  | {
      status: 'PENDING'
      paymentMethod?: never
    }
  | {
      status: 'CONFIRMED'
      paymentMethod: VendorPaymentMethodResponse
    }
